import { getNewTabActions } from '@components/ArtBoard/Footer/components/ArtboardTabs/getNewTabActions'
import { Badge, DropdownContainer } from '@lib/components'
import { CoreBatchAction, type TabItem } from '@octopus-do/octopus-project-core'
import { Button } from '@octopus-do/octopus-storybook'
import { Boards } from '@octopus-do/octopus-storybook/icons'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { commitProjectRequest } from '@storeOld/actionCreators'
import {
  CanvasActionTypes,
  setActiveTab,
  setTabIsCopied,
} from '@storeOld/reducers/canvas/canvas'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal, openSystemDialog } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtboardMode, useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { TABS_PERMISSION } from '@/permissions/project/definitions'
import { GlobalActions } from '@/store'
import type { AppState } from '@/types'
import { getId } from '@/utils/temporaryHelper'
import { MODAL_UPGRADE_PLAN } from '../../../../App/Modal/constants'
import { DELETE_TAB_DIALOG } from '../../../../App/SystemDialogs/constants'
import css from './ArtboardTabs.module.scss'
import { ArtboardTabsItem } from './ArtboardTabsItem'

export const ArtboardTabs: React.FC = () => {
  const dispatch = useDispatch()

  const { moveUpTab, moveDownTab, renameTab, deleteTab, duplicateTab } =
    useProjectLegacyActions()

  const isCopied = useSelector<AppState, boolean | null>(
    ({ canvas }) => canvas.tabs.isCopied
  )
  const renameInputIsActive = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.tabs.renameInputIsActive
  )
  const { hasNotAvailableAccessBadge, badgeType } = useProjectAccess(
    TABS_PERMISSION.CREATE
  )
  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  // The artboard is off: the assistant is working (a new tab mid-turn would
  // race its edits, OCT-4826), saving is down or the activity history is open.
  const artboardDisabled = useArtBoardDisabled()

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)

  const defaultColorId = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )

  const { seoMode, gallery } = useArtboardMode()

  const showBtn = !seoMode && !gallery

  const tabs = useSelector(migrationProjectSelector.getProjectTabs)

  const tabsLimit = tabs.length >= 3
  const disabledButton = tabsLimit || hasNotAvailableAccessBadge

  useEffect(() => {
    if (isCopied !== null && !isCopied) {
      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
        })
      )
    }

    if (isCopied !== null && isCopied) {
      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.COPY_SUCCESS,
        })
      )
    }
  }, [isCopied])

  const resetCanvas = (id?: string) => {
    dispatch(
      GlobalActions.updateCanvas({ scale: 1, translate: [0, 0], tabId: id })
    )

    dispatch({
      type: CanvasActionTypes.SET_SCALE,
      payload: {
        scale: 1,
      },
    })
  }

  const handleAddNewTab = (closeDropdown: () => void) => {
    if (disabledButton || artboardDisabled) {
      return
    }

    const tabId = getId()

    setTimeout(() => {
      dispatch(
        commitProjectRequest(
          CoreBatchAction({
            type: 'tab_creation',
            actions: getNewTabActions(tabId, defaultColorId),
          })
        )
      )

      dispatch(setActiveTab(tabId))
      sendGtmEvent(GtmTriggerName.PROJECT_TAB_ADD, 'Add new tab')

      resetCanvas()
      closeDropdown()
    }, 100)
  }

  const handleSetActiveTab = (id: string) => {
    if (tabs.length === 1) {
      return
    }
    const lc = sessionStorage.getItem(id)
    const canvasData = lc && JSON.parse(lc)

    dispatch(setActiveTab(id))

    if (canvasData) {
      dispatch(GlobalActions.updateCanvas({ ...canvasData, tabId: id }))
      dispatch({
        type: CanvasActionTypes.SET_SCALE,
        payload: {
          scale: canvasData.scale,
        },
      })
    } else {
      resetCanvas(id)
    }
  }

  const handleDelete = (id: string, name: string) => {
    dispatch(
      openSystemDialog(DELETE_TAB_DIALOG, {
        name,
        callback: (v: boolean) => {
          if (v) {
            const items = tabs.filter((s) => s.id !== id)
            const isActive = items.some((s) => s.id === activeTab)

            deleteTab({ id })

            if (!isActive) {
              handleSetActiveTab(items[0].id)
            }
          }
        },
      })
    )
  }

  const handleDuplicate = (id: string) => {
    if (disabledButton) {
      return
    }

    sendGtmEvent(GtmTriggerName.PROJECT_TAB_DUPLICATE, 'Duplicate tab')

    duplicateTab({ id })
  }

  const handleMoveUpTab = (id: string) => {
    moveUpTab({ id })
  }

  const handleMoveDownTab = (id: string) => {
    moveDownTab({ id })
  }

  const handleRenameTab = (id: string, name: string) => {
    if (name) {
      setTimeout(() => {
        renameTab({ id, name })
      }, 100)
    }
  }

  const handleCopy = (id: string) => {
    setTimeout(() => {
      // @ts-expect-error
      dispatch(setTabIsCopied(id))
    }, 100)
  }

  if (tabs.length === 0) {
    return null
  }

  return (
    <DropdownContainer
      position="top-start"
      offset={{ y: 8 }}
      customClassName={css.dropdown}
      noCloseByOutsideClick={renameInputIsActive}
      renderButton={(onSetRef, onClick, isOpen) => (
        <div className={css.button}>
          <Button
            ref={onSetRef}
            priority="secondary"
            size="medium"
            left={Boards}
            aria-label="Boards"
            active={isOpen}
            data-testid="sitemap_wrapper"
            onClick={onClick}
          />
        </div>
      )}
    >
      {(closeDropdown) => (
        <div>
          {tabs.map((tab: TabItem, index: number) => (
            <ArtboardTabsItem
              key={index}
              id={tab.id}
              activeTab={activeTab}
              isOneItem={tabs.length === 1}
              isFirstElement={index === 0}
              isLastElement={index === tabs.length - 1}
              disabledButton={disabledButton}
              disabledCopyButton={!projectId}
              isReadOnly={isReadOnly || artboardDisabled}
              onSetActive={handleSetActiveTab}
              onDelete={(id, name) => {
                handleDelete(id, name)
                closeDropdown()
              }}
              onDuplicate={handleDuplicate}
              onMoveUp={handleMoveUpTab}
              onMoveDown={handleMoveDownTab}
              onRenameTab={handleRenameTab}
              onCopyLink={handleCopy}
            />
          ))}

          {!tabsLimit && showBtn && !isReadOnly && (
            <div className={css.crateButtonWrap}>
              <button
                className={css.createButton}
                disabled={disabledButton || artboardDisabled}
                onClick={() => {
                  handleAddNewTab(closeDropdown)
                }}
              >
                Create new...
              </button>

              {hasNotAvailableAccessBadge && (
                <Badge
                  type={badgeType}
                  onClick={() => {
                    sendGtmEvent(GtmTriggerName.UPGRADE_TABS)
                    dispatch(openModal(MODAL_UPGRADE_PLAN))
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </DropdownContainer>
  )
}
